import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Wrench, Truck, Zap, Tractor, Tent } from 'lucide-react';
import axios from 'axios';
import { resolveImage } from '../lib/images';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categoryIcons = {
  Campsites: Tent,
  Trailers: Truck,
  Generators: Zap,
  Tractors: Tractor,
  'Tractor Attachments': Wrench,
  'Lawn Care': Wrench,
};

export const Equipment = () => {
  const [equipment, setEquipment] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [loading, setLoading] = useState(true);

  const categories = ['All', 'Trailers', 'Generators', 'Tractors', 'Tractor Attachments'];

  const fetchEquipment = useCallback(async () => {
    try {
      setLoading(true);
      const params = selectedCategory !== 'All' ? `?category=${selectedCategory}` : '';
      const response = await axios.get(`${API_URL}/api/equipment${params}`);
      // Exclude Campsites — those live on the "Book Your Stay" page now
      const items = (response.data.equipment || []).filter((item) => item.category !== 'Campsites');
      setEquipment(items);
    } catch (error) {
      // Error handled silently
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => {
    fetchEquipment();
  }, [fetchEquipment]);

  const filteredEquipment = equipment.filter((item) => {
    if (selectedCategory === 'All') return true;
    return item.category === selectedCategory;
  });

  return (
    <div className="min-h-screen pt-20 bg-[#050814]">
      {/* Hero Section */}
      <section className="relative h-72 flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `linear-gradient(rgba(5, 8, 20, 0.75), rgba(5, 8, 20, 0.9)), url('https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?auto=format&fit=crop&w=2000&q=80')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="container mx-auto px-4 z-10 text-center text-[#F4F1EA]">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Equipment Rentals</h1>
          <p className="text-xl text-[#B8860B]">Quality Tools for Every Job</p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-[#0B132B] border-b border-[#B8860B]/20">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => (
              <Button
                key={category}
                data-testid={`equipment-filter-${category.replace(/\s+/g, '-').toLowerCase()}`}
                onClick={() => setSelectedCategory(category)}
                className={`${
                  selectedCategory === category
                    ? 'bg-[#B8860B] text-[#050814] hover:bg-[#B8860B]/90'
                    : 'bg-transparent text-[#F4F1EA] border-2 border-[#B8860B]/40 hover:bg-[#B8860B]/10 hover:border-[#B8860B]'
                }`}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Equipment Grid */}
      <section className="py-16 bg-[#050814]">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="text-center py-20">
              <p className="text-lg text-[#F4F1EA]/70">Loading equipment...</p>
            </div>
          ) : filteredEquipment.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-lg text-[#F4F1EA]/70">No equipment found in this category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredEquipment.map((item) => {
                const Icon = categoryIcons[item.category] || Wrench;
                return (
                  <Card
                    key={item.id}
                    data-testid={`equipment-card-${item.id}`}
                    className="group overflow-hidden border border-[#B8860B]/30 bg-[#0B132B] shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
                  >
                    <div className="relative h-56 overflow-hidden bg-[#050814]">
                      <img
                        src={resolveImage(item.image_url) || 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=800'}
                        alt={item.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute top-4 right-4 bg-[#B8860B] text-[#050814] px-3 py-1 rounded-full text-sm font-semibold">
                        {item.category}
                      </div>
                    </div>
                    <CardContent className="p-6">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-10 h-10 bg-[#B8860B] rounded-full flex items-center justify-center flex-shrink-0 border border-[#B8860B]">
                          <Icon className="text-[#B8860B]" size={20} />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-[#F4F1EA] mb-1">{item.name}</h3>
                          <p className="text-sm text-[#F4F1EA]/60">{item.specifications}</p>
                        </div>
                      </div>
                      <p className="text-[#F4F1EA]/75 mb-4 line-clamp-2">{item.description}</p>

                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <p className="text-sm text-[#F4F1EA]/60">From</p>
                          <p className="text-2xl font-bold text-[#B8860B]">
                            ${item.daily_rate}
                            <span className="text-sm text-[#F4F1EA]/60">/day</span>
                          </p>
                        </div>
                        {item.weekly_rate > 0 && (
                          <div className="text-right">
                            <p className="text-sm text-[#F4F1EA]/60">Weekly</p>
                            <p className="text-lg font-semibold text-[#F4F1EA]">${item.weekly_rate}</p>
                          </div>
                        )}
                      </div>

                      <Link to={`/equipment/${item.id}`}>
                        <Button
                          data-testid={`equipment-book-btn-${item.id}`}
                          className="w-full bg-[#B8860B] hover:bg-[#B8860B]/90 text-[#F4F1EA] font-semibold"
                        >
                          {item.requires_tractor ? 'View Details' : 'Book Now'}
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 bg-[#0B132B] border-t border-[#B8860B]/20">
        <div className="container mx-auto px-4 text-center text-[#F4F1EA]">
          <h2 className="text-3xl font-bold mb-4 text-[#B8860B]">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
            {[
              { title: '1. Pick Your Dates', description: 'Choose your equipment and rental period' },
              { title: '2. Book Online', description: 'Secure checkout with instant confirmation' },
              { title: '3. Pick Up & Go', description: 'Equipment ready when you arrive' },
            ].map((step, idx) => (
              <div
                key={idx}
                className="p-6 bg-[#050814] rounded-lg border border-[#B8860B]/20 hover:border-[#B8860B] transition-colors"
              >
                <div className="w-14 h-14 bg-[#B8860B] rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-[#B8860B]">
                  <span className="text-[#B8860B] text-xl font-bold">{idx + 1}</span>
                </div>
                <h3 className="text-xl font-bold mb-2 text-[#F4F1EA]">{step.title}</h3>
                <p className="text-[#F4F1EA]/70">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
